import { useState, useRef, useEffect } from 'react';
import { TMDB_IMAGE_URLS } from '../constants';

const OptimizedImage = ({
  src,
  alt,
  className = '',
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  priority = false,
  placeholder = null,
  onLoad = () => {},
  onError = () => {}
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const [hasError, setHasError] = useState(false);
  const [currentSrcIndex, setCurrentSrcIndex] = useState(0);
  const imgRef = useRef(null);

  // Generate multiple fallback URLs for better image availability
  const generateImageSources = (originalSrc) => {
    if (!originalSrc) return [];
    
    const sources = [];
    
    // If it's already a full TMDB image URL, extract the path and rebuild it
    if (originalSrc.startsWith('http')) {
      // Check if it's a wrong domain (www.themoviedb.org) and fix it
      if (originalSrc.includes('www.themoviedb.org') || originalSrc.includes('themoviedb.org')) {
        // Extract the path (e.g., /4GIqZUgPZ146BhibsPHMHef2nXX.jpg)
        const pathMatch = originalSrc.match(/\/[a-zA-Z0-9]+\.(jpg|png|jpeg)/);
        if (pathMatch) {
          const path = pathMatch[0];
          sources.push(`${TMDB_IMAGE_URLS.W500}${path}`);
          sources.push(`${TMDB_IMAGE_URLS.W342}${path}`);
          sources.push(`${TMDB_IMAGE_URLS.ORIGINAL}${path}`);
        }
      } else if (originalSrc.includes('image.tmdb.org')) {
        // Already correct domain, use as-is
        sources.push(originalSrc);
      } else {
        // Other full URLs (not TMDB)
        sources.push(originalSrc);
      }
    }
    
    // If it's a TMDB path, try different sizes
    if (originalSrc.startsWith('/') || (originalSrc.includes('image.tmdb.org') && !sources.length)) {
      const path = originalSrc.startsWith('/') ? originalSrc : originalSrc.replace(/.*\/t\/p\/w\d+/, '');
      
      const tmdbSizes = ['w500', 'w342', 'w185', 'w154', 'w92', 'original'];
      tmdbSizes.forEach(size => {
        sources.push(`${TMDB_IMAGE_URLS.BASE}/${size}${path}`);
      });
    }
    
    // Remove duplicates while preserving order
    return [...new Set(sources)];
  };

  const imageSources = generateImageSources(src);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px 0px' // Start loading 50px before the image enters viewport
      }
    );

    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [priority]);

  // Reset when src changes
  useEffect(() => {
    setCurrentSrcIndex(0);
    setHasError(false);
    setIsLoaded(false);
  }, [src]);

  const handleLoad = () => {
    setIsLoaded(true);
    onLoad();
  };

  const handleError = () => {
    // Try next image source if available
    if (currentSrcIndex < imageSources.length - 1) {
      setCurrentSrcIndex(currentSrcIndex + 1);
    } else {
      // All sources failed
      setHasError(true);
      onError();
    }
  };

  // Don't render image until it's in view (unless priority)
  if (!isInView) {
    return (
      <div 
        ref={imgRef} 
        className={`${className} bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 animate-pulse flex items-center justify-center border border-gray-600`}
        aria-label="Loading image..."
      >
        {placeholder || (
          <div className="text-center p-4">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-xs text-gray-400">Loading...</p>
          </div>
        )}
      </div>
    );
  }

  if (hasError || imageSources.length === 0) {
    return (
      <div className={`${className} bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 flex items-center justify-center border border-gray-600`}>
        {placeholder || (
          <div className="text-center p-4">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-gray-600 to-gray-800 flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-xs text-gray-400">Image unavailable</p>
          </div>
        )}
      </div>
    );
  }

  const currentSrc = imageSources[currentSrcIndex];

  return (
    <div className={`relative ${className}`}>
      {/* Low quality placeholder while loading */}
      {!isLoaded && imageSources.length > 1 && (
        <img
          src={imageSources[imageSources.length - 1]} // Use smallest size as placeholder
          alt=""
          className="absolute inset-0 w-full h-full object-cover filter blur-sm opacity-30"
          aria-hidden="true"
        />
      )}
      
      {/* Main image with responsive sizes */}
      <img
        ref={imgRef}
        src={currentSrc}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-500 ${
          isLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        sizes={sizes}
        loading={priority ? 'eager' : 'lazy'}
        onLoad={handleLoad}
        onError={handleError}
      />
      
      {/* Loading skeleton */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 animate-pulse" />
      )}
    </div>
  );
};

export default OptimizedImage; 